import React, { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { PageTitle } from '../../atoms/PageTitle';
import { PageWrapper } from '../../atoms/PageWrapper';

type RequirePermissionProps = PropsWithChildren<{
  /** One permission, or several of which any lets in. */
  permission: string | string[];
  /** What a user without the permission is told; the data managers' wording by default. */
  message?: React.ReactNode;
}>;

/**
 * Renders its children only for a user whose access token grants `permission` (any of them, given several); anyone else gets a page
 * saying so, not a redirect (the pages they could be sent to would not help, and a redirect to sign-in
 * would loop — they are signed in). Signing in itself is the layout's (`withAuthenticationRequired`).
 */
export function RequirePermission({
  permission,
  message,
  children,
}: RequirePermissionProps) {
  const { isLoading, has } = usePermissions();

  if (isLoading) {
    return <div className="p-4">Checking your access…</div>;
  }
  const anyOf = Array.isArray(permission) ? permission : [permission];
  if (!anyOf.some(has)) {
    return <NotAuthorised permission={anyOf.join(' or ')} message={message} />;
  }
  return <>{children}</>;
}

export function NotAuthorised({
  permission,
  message,
}: {
  permission: string;
  message?: React.ReactNode;
}) {
  return (
    <PageWrapper className="max-w-xl">
      <PageTitle title="Not authorised" />
      {message ?? (
        <>
          <p className="mb-2">
            This page is for data managers. Your account does not have the{' '}
            <code>{permission}</code> permission.
          </p>
          <p className="mb-2">
            If you need it, ask an administrator to give your account the data
            manager role, then sign out and in again.
          </p>
        </>
      )}
      <Link className="text-dl-accent underline" to="/">
        Back to the home page
      </Link>
    </PageWrapper>
  );
}
